import fr from 'facet-rest';
import React from 'react';
import { FDSecondaryActionButton } from '@facet-wealth/design';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { dataAttrs } from 'Helpers/Testing';
import PropTypes from 'prop-types';
import { startAuthentication } from '@simplewebauthn/browser';
import { useSnackbarContext } from 'Context/SnackbarContext';

export const LoginWithPasskeyButton = ({ setAuthCollection, ButtonComponent }) => {
  const { resource } = useMetaLayoutContext();
  const { displayErrorMessage } = useSnackbarContext();

  const loginWithPasskey = async (rel) => {
    try {
      // Ask the server for a challenge
      const [challenge] = await rel.link('related').get();

      // Pass the challenge to the browser's authenticator which will prompt the user to choose a
      // passkey that will sign the challenge.
      const credential = await startAuthentication({ optionsJSON: challenge.attr('options').publicKey });

      // Send the signed challenge to the server for verification.
      // This uses fetch because the webauthn library requires a full HTTP request to be passed in, so they
      // dictate everything about it.  Our normal FR stuff won't be recognized by the webauthn lib.
      const res = await fetch(challenge.relationship('passkeyLoginVerification').link('related').attr('url'), {
        credentials: 'include',
        mode: 'cors',
        method: 'POST',
        body: JSON.stringify(credential),
      });

      if (!res.ok) {
        throw new Error('Failed to validate passkey');
      }

      const json = await res.json();
      const coll = fr.collection(json); // convert back to FR-land
      setAuthCollection(coll); // this should be a user collection which will render the dashboard
    } catch (err) {
      // If the user clicks cancel on the passkey window, a NotAllowedError is throw.  We don't want to
      // display an error message in that case.
      if (err.name !== 'NotAllowedError') {
        displayErrorMessage(err, 'Failed to validate passkey');
      }
    }
  };

  return resource.withRelationship(
    fr.relationships.passkeyLoginChallenge.name,
    (rel) => (
      <ButtonComponent
        icon={rel.meta('icon')}
        isFullWidth
        {...dataAttrs('login-with-passkey-btn', resource)}
        onClick={() => loginWithPasskey(rel)}
        label={rel.meta('prompt')}
      />
    ),
    () => null,
  );
};

LoginWithPasskeyButton.defaultProps = {
  ButtonComponent: FDSecondaryActionButton,
};

LoginWithPasskeyButton.propTypes = {
  ButtonComponent: PropTypes.func,
  setAuthCollection: PropTypes.func.isRequired,
};
